"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import clsx from "clsx";
import { isLocale, locales, type Locale } from "@/lib/i18n";
import { getWeddingBasePath } from "@/lib/wedding-routes";

type LanguageSwitcherProps = {
  current: Locale;
  className?: string;
  linkClassName?: string;
  activeClassName?: string;
  ariaLabel?: string;
};

function getWeddingRestPath(pathname: string | null): string[] {
  if (!pathname) return [];

  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "wedding") {
    return [];
  }

  if (segments[1] && isLocale(segments[1])) {
    return segments.slice(2);
  }

  return segments.slice(1);
}

export function LanguageSwitcher({
  current,
  className,
  linkClassName,
  activeClassName,
  ariaLabel,
}: LanguageSwitcherProps) {
  const pathname = usePathname();
  const [hash, setHash] = useState("");
  const resolvedAriaLabel =
    ariaLabel ??
    (current === "fr"
      ? "Changer de langue"
      : current === "en"
        ? "Switch language"
        : "Sprache wechseln");

  useEffect(() => {
    setHash(window.location.hash);

    const onHashChange = () => {
      setHash(window.location.hash);
    };

    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const restPath = getWeddingRestPath(pathname);

  function getPathForLocale(locale: Locale) {
    const basePath = getWeddingBasePath(locale);
    const suffix = restPath.length ? `/${restPath.join("/")}` : "";
    return `${basePath}${suffix}${hash}`;
  }

  return (
    <nav aria-label={resolvedAriaLabel} className={className}>
      {locales.map((locale) => (
        <Link
          key={locale}
          href={getPathForLocale(locale)}
          className={clsx(linkClassName, locale === current && activeClassName)}
        >
          {locale.toUpperCase()}
        </Link>
      ))}
    </nav>
  );
}
